import jsPDF from "jspdf";
import "jspdf-autotable";
import "../../../../assets/font/THSarabunNew-normal";
import "../../../../assets/font/THSarabunNew-bold";
import logoLeasing from "../../../../assets/images/drawable-header.png";
import logoMoney from "../../../../assets/images/money.png";
import logoKSM from "../../../../assets/images/ksm.png";
import dayjs from "dayjs";

// ============================================================
// SHARED UTILS
// ============================================================

const findSignatureImage = async (imageList, userId) => {
  if (!imageList || !userId) return null;
  const expectedName = `lawyer/user/license_lawyer/public/${userId}.png`;
  const signature = imageList.find((item) => item.name === expectedName);
  if (!signature) return null;
  try {
    const response = await fetch(signature.url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const getCompanyData = (companiesList, companyId) => {
  if (!companiesList || !companyId) return null;
  const idMap = { 4: 1, 5: 2 }; // 4 ใช้ 1, 5 ใช้ 2
  const id = idMap[companyId] || companyId;
  return companiesList.find((c) => c.id === id);
};

const getApproverInfo = (currentUserId, nameLawyers) => {
  const approverMapping = { 16: 13, 17: 14, 18: 15 };
  const approverId = approverMapping[currentUserId] || null;
  if (!approverId) return null;
  const approver = nameLawyers?.find((item) => item.id === approverId);
  return { approverId, approverData: approver };
};

const canApprove = (currentUserId) => {
  return [16, 17, 18, 1, 6].includes(parseInt(currentUserId));
};

const groupByTMBILL = (selectedRows) => {
  const grouped = {};
  selectedRows.forEach((row) => {
    if (!grouped[row.TMBILL]) grouped[row.TMBILL] = [];
    grouped[row.TMBILL].push(row);
  });
  return grouped;
};

const prepareTableData = (
  tmbill,
  billDetail,
  convertDateThai,
  currencyFormatPoint,
) => {
  const billDataList = billDetail.filter((item) => item.TMBILL === tmbill);
  if (!billDataList?.length)
    return { tableData: [], totalRequested: 0, totalActual: 0, contnoCount: 0 };

  const groupedByContno = {};
  let totalRequested = 0;
  let totalActual = 0;

  billDataList.forEach((billData) => {
    const blackNo = billData.blackCaseNumber;
    console.log("blackNo----->", blackNo);

    if (!billData.workExpenseList?.length) return;
    billData.workExpenseList.forEach((expense) => {
      const contno = expense.CONTNO || "-";
      if (!groupedByContno[contno]) {
        groupedByContno[contno] = {
          CONTNO: contno + ` ${blackNo ? `(${blackNo})` : ""}`,
          created_date: expense.PAYDT
            ? dayjs(expense.PAYDT).add(543, "year").format("DD MMM YYYY")
            : expense.NETPAY_DT
              ? dayjs(expense.NETPAY_DT).add(543, "year").format("DD MMM YYYY")
              : "-",
          expenses: [],
          subtotalRequested: 0,
          subtotalActual: 0,
        };
      }
      const netpay = parseFloat(expense.NETPAY) || 0;
      const pay = parseFloat(expense.PAY) || 0;
      groupedByContno[contno].expenses.push({
        description: expense.description || "-",
        amountRequested: netpay,
        amountActual: pay,
        expenseTypeId: expense.expenseTypeId,
      });
      groupedByContno[contno].subtotalRequested += netpay;
      groupedByContno[contno].subtotalActual += pay;
      totalRequested += netpay;
      totalActual += pay;
    });
  });

  const tableData = [];
  let rowIndex = 1;

  Object.values(groupedByContno).forEach((group) => {
    group.expenses.sort((a, b) => a.expenseTypeId - b.expenseTypeId);
    group.expenses.forEach((expense, i) => {
      tableData.push([
        i === 0 ? rowIndex.toString() : "",
        i === 0 ? group.CONTNO : "",
        i === 0 ? group.created_date : "",
        expense.description,
        currencyFormatPoint(expense.amountRequested),
        currencyFormatPoint(expense.amountActual),
      ]);
    });
    tableData.push([
      "",
      "",
      "",
      "รวม",
      currencyFormatPoint(group.subtotalRequested),
      currencyFormatPoint(group.subtotalActual),
    ]);
    rowIndex++;
  });

  return {
    tableData,
    totalRequested,
    totalActual,
    contnoCount: Object.keys(groupedByContno).length,
  };
};

// ============================================================
// RECEIPT UTILS
// ============================================================

const formatDateThai = (dateStr) => {
  if (!dateStr) return "-";
  const d = dayjs(dateStr);
  const months = [
    "ม.ค.",
    "ก.พ.",
    "มี.ค.",
    "เม.ย.",
    "พ.ค.",
    "มิ.ย.",
    "ก.ค.",
    "ส.ค.",
    "ก.ย.",
    "ต.ค.",
    "พ.ย.",
    "ธ.ค.",
  ];
  return `${d.date()} ${months[d.month()]} ${d.year() + 543}`;
};

const drawDottedLine = (pdf, x1, y, x2) => {
  pdf.setLineDashPattern([1, 1], 0);
  pdf.setLineWidth(0.3);
  pdf.line(x1, y, x2, y);
  pdf.setLineDashPattern([], 0);
};

const drawCheckbox = (pdf, x, y, checked) => {
  const size = 3.5;
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.3);
  pdf.rect(x, y, size, size);
  if (checked) {
    pdf.setLineWidth(0.5);
    pdf.line(x + 0.5, y + 1.8, x + 1.5, y + 2.8);
    pdf.line(x + 1.5, y + 2.8, x + 3, y + 0.5);
  }
};

// ✅ วาด checkmark กลางเซลล์ (ใช้ใน didDrawCell)
const drawCellCheckmark = (pdf, cell) => {
  const x = cell.x + cell.width / 2 - 1.5;
  const y = cell.y + cell.height / 2 - 1.5;
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.5);
  pdf.line(x, y + 1.5, x + 1.2, y + 3);
  pdf.line(x + 1.2, y + 3, x + 3.5, y + 0.5);
};

// ============================================================
// วาดหน้าใบเคลียร์เงินทดรองจ่าย
// ============================================================
const drawReceiptPage = (
  pdf,
  { record, currencyFormatPoint, companyData, dataExport },
) => {
  const str = (val) => (val === null || val === undefined ? "" : String(val));

  const setFont = (style = "normal", size = 12) => {
    pdf.setFont("THSarabunNew", style);
    pdf.setFontSize(size);
  };

  // record อาจเป็น array (หลายรายการ) หรือ object (รายการเดียว)
  const records = Array.isArray(record) ? record : [record];
  const header = records[0];

  const pageW = 210;
  const mL = 5;
  const mR = 5;
  const cW = pageW - mL - mR;
  const rX = mL + cW;

  // ============ TITLE ============
  setFont("bold", 18);
  pdf.text(str(companyData?.company_name), pageW / 2, 12, { align: "center" });
  setFont("bold", 16);
  pdf.text("ใบเคลียร์เงินทดรองจ่าย", pageW / 2, 20, { align: "center" });

  // ============ INFO RIGHT ============
  setFont("normal", 13);
  pdf.text("เลขที่เอกสาร", rX - 55, 30);
  setFont("bold", 13);
  pdf.text(str(dataExport?.CL_DOCNO || "-"), rX - 25, 30);

  setFont("normal", 13);
  pdf.text("วันที่เอกสาร", rX - 55, 36);
  setFont("bold", 13);
  pdf.text(str(formatDateThai(dataExport?.CL_DOCDT)), rX - 25, 36);

  setFont("normal", 13);
  pdf.text("อ้างอิงเลขที่ใบเบิก", rX - 55, 42);
  setFont("bold", 13);
  pdf.text(str(dataExport?.DOCNO || "-"), rX - 25, 42);

  // ============ INFO LEFT ============
  setFont("normal", 13);
  pdf.text("รหัสสาขา", mL, 36);
  setFont("bold", 13);
  const locatText = header?.LOCAT_NAME
    ? `${header.LOCAT_NAME} (${header.LOCAT})`
    : str(header?.LOCAT || "-");
  pdf.text(locatText, mL + 22, 36);

  setFont("normal", 13);
  pdf.text("พนักงาน", mL, 42);
  setFont("bold", 13);
  const employeeText = header?.NAME_LAWYER
    ? `${header.NAME_LAWYER}${header?.OFFCODE ? ` (${header.OFFCODE})` : ""}`
    : str("-");
  pdf.text(employeeText, mL + 22, 42);

  // ============ TABLE ============
  // ✅ ใช้ "CHECK_TAX" / "CHECK_BIL" เป็น marker string
  //    แล้ววาด checkmark จริงใน didDrawCell แทน ✓ ที่ font ไม่รองรับ
  const tableBody = records.map((item, i) => [
    str(i + 1) + ".",
    str(item.DESCRIPTION || item.MEMO1 || "-"),
    str(item.LOCAT || "-"),
    item.CHKTAX === "Y" ? "CHECK_TAX" : "", // ← marker
    item.CHKBIL === "Y" ? "CHECK_BIL" : "", // ← marker
    currencyFormatPoint
      ? str(currencyFormatPoint(item.AMOUNT || 0))
      : str(
          Number(item.AMOUNT || 0).toLocaleString("th-TH", {
            minimumFractionDigits: 2,
          }),
        ),
  ]);

  pdf.autoTable({
    head: [["No.", "รายการ", "รหัสแผนก", "ภาษีซื้อ", "ใบเสร็จ", "จำนวนเงิน"]],
    body: tableBody,
    startY: 46,
    styles: {
      font: "THSarabunNew",
      fontSize: 13,
      cellPadding: { top: 2, bottom: 2, left: 3, right: 3 },
      lineColor: [0, 0, 180],
      lineWidth: 0.25,
      textColor: [0, 0, 0],
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      fontSize: 13,
      halign: "center",
      lineColor: [0, 0, 180],
      lineWidth: 0.25,
    },
    columnStyles: {
      0: { halign: "center", cellWidth: 12 },
      1: { halign: "left", cellWidth: 93 },
      2: { halign: "center", cellWidth: 25 },
      3: { halign: "center", cellWidth: 20 },
      4: { halign: "center", cellWidth: 20 },
      5: { halign: "right", cellWidth: 30 },
    },
    bodyStyles: {
      minCellHeight: 8,
      lineColor: [0, 0, 180],
      lineWidth: { top: 0, right: 0.25, bottom: 0, left: 0.25 },
    },
    margin: { left: mL, right: mR },
    tableLineColor: [0, 0, 180],
    tableLineWidth: 0.25,

    // ✅ แปลง marker เป็น checkmark วาดด้วย line จริง
    didParseCell(data) {
      if (
        data.section === "body" &&
        (data.cell.raw === "CHECK_TAX" || data.cell.raw === "CHECK_BIL")
      ) {
        data.cell.text = [""];
      }
    },
    didDrawCell(data) {
      if (
        data.section === "body" &&
        (data.cell.raw === "CHECK_TAX" || data.cell.raw === "CHECK_BIL")
      ) {
        drawCellCheckmark(pdf, data.cell);
      }
    },
  });

  let y = pdf.lastAutoTable.finalY;

  // เส้นขอบล่างตาราง
  pdf.setDrawColor(0, 0, 180);
  pdf.setLineWidth(0.25);
  pdf.line(mL, y, mL + cW, y);

  // ============ SUMMARY SECTION ============
  const summaryH = 28;
  pdf.setDrawColor(0, 0, 180);
  pdf.setLineWidth(0.25);
  pdf.rect(mL, y, cW, summaryH);

  const splitX = mL + 105;
  pdf.line(splitX + 65, y, splitX + 65, y + summaryH);

  const netpay = parseFloat(header?.NETPAY || 0);
  const totpay = parseFloat(header?.TOTPAY || 0);
  const advpay = parseFloat(header?.ADVPAYAMT || 0);
  const rawDiff = netpay !== 0 ? netpay : advpay - totpay;
  const diff = Math.abs(rawDiff);
  const isClaim = rawDiff >= 0;

  const fmt = (v) =>
    str(
      currencyFormatPoint
        ? currencyFormatPoint(v)
        : Number(v || 0).toLocaleString("th-TH", { minimumFractionDigits: 2 }),
    );

  // ฝั่งซ้าย แถว 1: คืนเงิน / เบิกเพิ่ม
  drawCheckbox(pdf, mL + 5, y + 5, isClaim);
  setFont("bold", 13);
  pdf.text("คืนเงิน", mL + 12, y + 8.5);
  drawCheckbox(pdf, mL + 40, y + 5, !isClaim);
  pdf.text("เบิกเพิ่ม", mL + 47, y + 8.5);
  setFont("bold", 20);
  pdf.text(fmt(diff), splitX - 20, y + 12, { align: "right" });

  // ฝั่งซ้าย แถว 2: เงินสด / เงินโอน
  const isCash = header?.CHKPAY === "1" || header?.CHKFLAG === "1";
  const isTransfer = header?.CHKPAY === "2" || header?.CHKFLAG === "2";
  drawCheckbox(pdf, mL + 5, y + 16, isCash);
  setFont("normal", 13);
  pdf.text("เงินสด", mL + 12, y + 19.5);
  drawCheckbox(pdf, mL + 40, y + 16, isTransfer);
  pdf.text("เงินโอน", mL + 47, y + 19.5);

  // ฝั่งขวา: 3 บรรทัดยอด
  setFont("normal", 13);
  pdf.text("ค่าใช้จ่ายที่เกิดขึ้นจริง", splitX + 37, y + 7);
  setFont("bold", 13);
  pdf.text(fmt(totpay), rX - 2, y + 7, { align: "right" });

  setFont("normal", 13);
  pdf.text("จำนวนเงินที่ขอเบิก", splitX + 39, y + 14);
  setFont("bold", 13);
  pdf.text(fmt(advpay), rX - 2, y + 14, { align: "right" });

  setFont("normal", 13);
  pdf.text("เงินสดคงเหลือ/ขาด", splitX + 38, y + 21);
  setFont("bold", 13);
  pdf.text(fmt(diff), rX - 2, y + 21, { align: "right" });

  y += summaryH;

  // ============ BANK INFO ============
  y += 2;
  const bankName = str(header?.BANKLOCNAM?.split("/")[0]?.trim() || "");
  const branchName = str(
    header?.BKADDR1 || header?.BANKLOCNAM?.split("/")[1]?.trim() || "",
  );

  pdf.setDrawColor(0, 0, 180);
  pdf.setLineWidth(0.25);
  pdf.rect(mL, y, cW, 10);

  setFont("normal", 12);
  pdf.text("ชื่อธนาคาร", mL + 2, y + 6.5);
  setFont("bold", 12);
  pdf.text(bankName, mL + 22, y + 6.5);
  setFont("normal", 12);
  pdf.text(str(header?.BOOKNO || ""), mL + 65, y + 6.5);
  pdf.text("สาขา", mL + 100, y + 6.5);
  setFont("bold", 12);
  pdf.text(branchName, mL + 113, y + 6.5);

  y += 10;

  // ============ SIGNATURE ============
  const sigH = 14;
  const col1W = cW / 2;

  pdf.setDrawColor(0, 0, 180);
  pdf.setLineWidth(0.25);
  pdf.rect(mL, y, cW, sigH);
  pdf.line(mL + col1W, y, mL + col1W, y + sigH);

  setFont("normal", 13);
  pdf.text("ผู้อนุมัติ :", mL + 4, y + 5.5);
  drawDottedLine(pdf, mL + 22, y + 6, mL + col1W - 4);

  setFont("normal", 13);
  pdf.text("เจ้าหน้าที่เงินทดรองจ่าย :", mL + col1W + 4, y + 5.5);
  drawDottedLine(pdf, mL + col1W + 48, y + 6, mL + cW - 4);

  setFont("normal", 11);
  pdf.text("วันที่", mL + 4, y + 11);
  pdf.text("__/__/__", mL + 14, y + 11);
  pdf.text("วันที่", mL + col1W + 4, y + 11);
  pdf.text("__/__/__", mL + col1W + 14, y + 11);
};

// ============================================================
// MAIN EXPORT: generatePDF (ใบเคลียร์ + ใบเสร็จ)
// ============================================================
export const generatePDF = async ({
  dataExport,
  selectedRows,
  billDetail,
  nameLawyers,
  companiesList,
  imageList,
  convertDateThai,
  currencyFormatPoint,
  advanceClearPayData = null,
}) => {
  const pdf = new jsPDF();
  const currentUserId = parseInt(localStorage.getItem("USER_ID"));
  const groupedData = groupByTMBILL(selectedRows);

  // ✅ ถ้ามี advanceClearPayData → วาดใบเสร็จหน้าแรกก่อน
  let receiptDrawnFirst = false;

  const hasReceipt = Array.isArray(advanceClearPayData)
    ? advanceClearPayData.length > 0
    : !!advanceClearPayData;

  if (hasReceipt) {
    const firstCompanyData = getCompanyData(
      companiesList,
      selectedRows[0].COMPANY_ID,
    );
    if (firstCompanyData) {
      drawReceiptPage(pdf, {
        record: advanceClearPayData,
        currencyFormatPoint,
        companyData: firstCompanyData,
        dataExport: dataExport[0],
      });
      receiptDrawnFirst = true;
    }
  }

  // วนลูปใบเคลียร์เงินทดรองจ่าย
  let pageIndex = 0;

  for (const [tmbill, records] of Object.entries(groupedData)) {
    const record = records[0];

    if (receiptDrawnFirst || pageIndex !== 0) pdf.addPage();
    pageIndex++;

    let pdfPositionY = 0;
    const pdfPositionX = 10;
    const pdfPositionXCenter = 150;
    let imageWidth = 45;
    let imageHeight = 25;
    const imageWidthSign = 25;
    const imageHeightSign = 15;

    const companyData = getCompanyData(companiesList, record.COMPANY_ID);
    if (!companyData) {
      console.warn(`ไม่พบข้อมูลบริษัทสำหรับ COMPANY_ID: ${record.COMPANY_ID}`);
      continue;
    }

    const imageUrl =
      companyData.id === 1
        ? logoLeasing
        : companyData.id === 2
          ? logoMoney
          : companyData.id === 3
            ? logoKSM
            : logoLeasing;

    if (companyData.id === 2) pdfPositionY += 5;
    else if (companyData.id === 3) imageHeight = 30;

    pdf.addImage(
      imageUrl,
      "PNG",
      pdfPositionXCenter,
      pdfPositionY,
      imageWidth,
      imageHeight,
    );

    pdf.setFont("THSarabunNew", "bold");
    pdf.setFontSize(14);
    pdf.text(`วันที่พิมพ์ ${convertDateThai()}`, pdfPositionX, 10);
    pdf.text(`เลขที่อ้างอิง ${tmbill || "-"}`, pdfPositionX, 15);
    pdf.setTextColor(150, 150, 150);
    pdf.text(
      `${records[0].flagPrintClear > 0 ? "[สำเนา]" : "[ต้นฉบับ]"}`,
      pdfPositionX + 90,
      10,
    );
    pdf.setTextColor(0, 0, 0);

    pdf.setFontSize(16);
    if (companyData.id === 1) {
      pdfPositionY += 30;
      pdf.text(
        `${companyData.company_name}`,
        pdfPositionXCenter - 11,
        pdfPositionY,
      );
      pdf.text(`${companyData.address}`, 95, (pdfPositionY += 8));
    } else if (companyData.id === 2) {
      pdfPositionY += 33;
      pdf.text(
        `${companyData.company_name}`,
        pdfPositionXCenter - 10,
        pdfPositionY,
      );
      pdf.text(
        `${companyData.address}`,
        pdfPositionXCenter - 54,
        (pdfPositionY += 8),
      );
    } else if (companyData.id === 3) {
      pdfPositionY += 25;
      pdf.text(
        `${companyData.company_name}`,
        pdfPositionXCenter - 40,
        (pdfPositionY += 3),
      );
      pdf.text(
        `${companyData.address}`,
        pdfPositionXCenter - 60,
        (pdfPositionY += 8),
      );
    }

    pdfPositionY += 10;
    pdf.text("ใบเคลียร์เงินทดรองจ่าย", pdfPositionX + 70, pdfPositionY);
    pdf.setTextColor(0, 0, 0);
    pdfPositionY += 5;

    const { tableData, totalRequested, totalActual, contnoCount } =
      prepareTableData(
        tmbill,
        billDetail,
        convertDateThai,
        currencyFormatPoint,
      );

    if (tableData.length === 0) {
      console.warn(`ไม่มีข้อมูลสำหรับ TMBILL: ${tmbill}`);
      continue;
    }

    const difference = totalRequested - totalActual;
    const differenceText =
      difference > 0 ? "คืนเงิน" : difference < 0 ? "เบิกเพิ่ม" : null;

    const finalRows = [];
    if (contnoCount > 1) {
      finalRows.push([
        "",
        "",
        "",
        "รวมยอดสุทธิ",
        currencyFormatPoint(totalRequested),
        currencyFormatPoint(totalActual),
      ]);
    }
    finalRows.push([
      "",
      "",
      "",
      differenceText,
      "",
      currencyFormatPoint(Math.abs(difference)),
    ]);

    pdf.autoTable({
      head: [
        [
          "ลำดับ",
          "เลขที่สัญญา",
          "วันที่เคลียร์",
          "รายการ",
          "จำนวนเบิก(บาท)",
          "จำนวนจริง(บาท)",
        ],
      ],
      body: [...tableData, ...finalRows],
      startY: pdfPositionY,
      styles: { font: "THSarabunNew", fontSize: 14, cellPadding: 3 },
      headStyles: {
        fillColor: [0, 102, 204],
        textColor: [255, 255, 255],
        fontSize: 14,
        halign: "center",
        fontStyle: "bold",
      },
      columnStyles: {
        0: { halign: "center", cellWidth: 15 },
        1: { halign: "center", cellWidth: 45 },
        2: { halign: "center", cellWidth: 30 },
        3: { halign: "left", cellWidth: 40 },
        4: { halign: "right", cellWidth: 30 },
        5: { halign: "right", cellWidth: 30 },
      },
      margin: { top: 10, left: 10, right: 10 },
      didParseCell(data) {
        const totalRowsCount = tableData.length + finalRows.length;
        const rowIndex = data.row.index;
        if (
          data.section === "body" &&
          data.cell.raw === "รวม" &&
          data.column.index === 3
        )
          data.cell.styles.fontStyle = "bold";
        if (
          data.section === "body" &&
          data.cell.raw === "รวมยอดสุทธิ" &&
          data.column.index === 3
        )
          data.cell.styles.fontStyle = "bold";
        if (data.section === "body" && rowIndex === totalRowsCount - 1) {
          data.cell.styles.fontStyle = "bold";
          if (difference > 0) data.cell.styles.textColor = [255, 0, 0];
          else if (difference < 0) data.cell.styles.textColor = [0, 128, 0];
        }
      },
    });

    const finalY = pdf.lastAutoTable.finalY;
    pdf.setTextColor(0, 0, 0);
    pdf.setFont("THSarabunNew", "normal");
    pdf.setFontSize(14);

    const lawyer = nameLawyers?.find((item) => item.id === record.userId);
    const lawyerSignature = await findSignatureImage(imageList, record.userId);

    if (lawyerSignature)
      pdf.addImage(
        lawyerSignature,
        "PNG",
        55,
        finalY + 7,
        imageWidthSign,
        imageHeightSign,
      );

    pdf.text(
      "ลงชื่อผู้เบิก...................................",
      40,
      finalY + 20,
    );
    pdf.text(
      `${lawyer?.FNAME || "-"}  ${lawyer?.LNAME || ""}`,
      45,
      finalY + 27,
    );
    if (lawyer?.book_bank) pdf.text(`${lawyer.book_bank}`, 45, finalY + 32);
    if (lawyer?.telp) pdf.text(`${lawyer?.telp}`, 45, finalY + 37);

    let approverInfo = null;
    let approverSignature = null;

    if (record.clearStatus === 1 && record.clearBy) {
      const approverData = nameLawyers?.find(
        (l) => l.id === parseInt(record.clearBy),
      );
      if (approverData) {
        approverInfo = { approverId: parseInt(record.clearBy), approverData };
        approverSignature = await findSignatureImage(
          imageList,
          approverInfo.approverId,
        );
      }
    } else if (canApprove(currentUserId)) {
      approverInfo = getApproverInfo(currentUserId, nameLawyers);
      if (approverInfo)
        approverSignature = await findSignatureImage(
          imageList,
          approverInfo.approverId,
        );
    }

    pdf.text(
      "ลงชื่อผู้อนุมัติ...................................",
      120,
      finalY + 20,
    );

    if (record.clearStatus === 1 && record.clearDate && approverInfo) {
      if (approverSignature)
        pdf.addImage(
          approverSignature,
          "PNG",
          140,
          finalY + 4,
          imageWidthSign,
          imageHeightSign,
        );
      const approverFullName = `${approverInfo.approverData?.FNAME || ""} ${approverInfo.approverData?.LNAME || ""}`;
      pdf.text(`(${approverFullName})`, 135, finalY + 27);
      pdf.text(
        `วันที่อนุมัติ ${convertDateThai(dayjs(record.clearDate))}`,
        125,
        finalY + 32,
      );
    } else {
      pdf.text("(                                     )", 125, finalY + 27);
    }

    pdf.text(
      "ลงชื่อผู้ตรวจ..................................",
      120,
      finalY + 50,
    );
  }

  // เปิด PDF
  const pdfBlob = pdf.output("blob");
  const pdfUrl = URL.createObjectURL(pdfBlob);
  const newWindow = window.open(pdfUrl);
  if (newWindow) {
    newWindow.onload = () => newWindow.print();
  } else {
    alert("กรุณาปิดการบล็อกป๊อปอัปเพื่อใช้งานฟังก์ชันนี้");
  }
};
